/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useMemo,
  useCallback,
  useState,
  useEffect,
  ReactElement,
  JSXElementConstructor,
  ReactNode,
  ReactPortal,
  useRef,
} from 'react';
import { Marker } from 'react-map-gl/maplibre';
import { atom, useAtomValue } from 'jotai';
import { Source, Layer } from 'react-map-gl/maplibre';
import * as turf from '@turf/turf';

import { JobLocationCoordinates } from '@restworld/data-services';

import { explorerRestaurantMapMarkersAtom } from '../../job-explorer-restaurant-explorer.atoms';
import {
  getPopupLocationCoordinatesAtom,
  getMapRefAtom,
  jobLocationCoordinatesAtom,
} from '../../job-explorer-map.atoms';
import MarkerWithFloatingPopup from '../job-explorer-map.components/marker-with-floating-popup';
import { isRestaurantMarkerAtom } from '../popup-info-el';
import { cn } from '@restworld/utils-common';

const ClusterMarker = ({ className, ...props }: { limit?: number } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'rounded-full bg-white shadow-lg text-black border border-gray-200 flex items-center justify-center text-base cursor-pointer h-4 w-4 ;',
      className
    )}
    {...props}
  />
);
export type JobLocationCoordinatesClusterType = {
  properties: {
    cluster: any;
    point_count:
      | string
      | number
      | boolean
      | ReactElement<any, string | JSXElementConstructor<any>>
      | Iterable<ReactNode>
      | ReactPortal;
  };
  extra?: JobLocationCoordinates;
  id?: string | number;
  geometry: { coordinates: number[] };
};

export const clusterRadiusAtom = atom(40);
export const clusterMaxZoomAtom = atom(14);
export const clusterMinPointsAtom = atom(2);

// Helper to create a worker for Supercluster
function createSuperclusterWorker() {
  const workerCode = `
    self.importScripts('https://unpkg.com/supercluster@8.0.1/dist/supercluster.min.js');
    let supercluster = null;
    self.onmessage = function(e) {
      const { type, payload } = e.data;
      if (type === 'load') {
        supercluster = new Supercluster(payload.options);
        supercluster.load(payload.points);
        self.postMessage({ type: 'loaded' });
      } else if (type === 'getClusters') {
        // Always post a reply, even when the index is gone or the query throws: a request with no
        // response leaves the caller's Promise pending forever (worker torn down / mid-reload).
        try {
          const clusters = supercluster ? supercluster.getClusters(payload.bbox, payload.zoom) : [];
          self.postMessage({ type: 'clusters', clusters });
        } catch (err) {
          self.postMessage({ type: 'clusters', clusters: [] });
        }
      } else if (type === 'getLeaves') {
        try {
          const leaves = supercluster ? supercluster.getLeaves(payload.clusterId, payload.limit, payload.offset) : [];
          self.postMessage({ type: 'leaves', leaves });
        } catch (err) {
          // A clusterId that went stale after a reload throws "No cluster with the specified id".
          self.postMessage({ type: 'leaves', leaves: [] });
        }
      } else if (type === 'getClusterExpansionZoom') {
        try {
          const zoom = supercluster ? supercluster.getClusterExpansionZoom(payload.clusterId) : 0;
          self.postMessage({ type: 'expansionZoom', zoom });
        } catch (err) {
          // Stale clusterId after a reload throws "No cluster with the specified id"; reply with 0
          // so the caller falls back to a default fly-in zoom instead of hanging on the Promise.
          self.postMessage({ type: 'expansionZoom', zoom: 0 });
        }
      }
    };
  `;
  const blob = new Blob([workerCode], { type: 'application/javascript' });
  return new Worker(URL.createObjectURL(blob));
}

const useMarkers = ({ explorerType }: { explorerType?: 'restaurants' | 'job-positions' }) => {
  const jobCoordinates = useAtomValue(jobLocationCoordinatesAtom);
  const explorerRestaurantMapMarkers = useAtomValue(explorerRestaurantMapMarkersAtom);

  const processMarkers = useCallback((markers: JobLocationCoordinates[], isRestaurantMarker: boolean) => {
    const offsetDistance = 0.00001;
    const seenCoordinates = new Set<string>();
    return (markers || []).reduce((acc, marker) => {
      if (marker.coordinates.lat !== null && marker.coordinates.lon !== null) {
        let { lat, lon } = marker.coordinates;
        let key = `${lat},${lon}`;
        while (seenCoordinates.has(key)) {
          lat += offsetDistance;
          lon += offsetDistance;
          key = `${lat},${lon}`;
        }
        seenCoordinates.add(key);
        return [
          ...acc,
          {
            ...marker,
            coordinates: { ...marker.coordinates, lat, lon },
            ...(isRestaurantMarker ? { is_restaurant_marker: true } : { is_job_position_marker: true }),
          },
        ];
      }
      return acc;
    }, [] as JobLocationCoordinates[]);
  }, []);

  return useMemo(() => {
    if (explorerType === 'restaurants') return processMarkers(explorerRestaurantMapMarkers, true);
    if (explorerType === 'job-positions') return processMarkers(jobCoordinates, false);
    // Combine both marker types — show whichever is available
    const jpMarkers = jobCoordinates?.length ? processMarkers(jobCoordinates, false) : [];
    const restaurantMarkers = explorerRestaurantMapMarkers?.length
      ? processMarkers(explorerRestaurantMapMarkers, true)
      : [];
    return [...jpMarkers, ...restaurantMarkers];
  }, [explorerRestaurantMapMarkers, explorerType, jobCoordinates, processMarkers]);
};

const useBufferedArea = () => {
  const popupLocationCoordinates = useAtomValue(getPopupLocationCoordinatesAtom);

  return useMemo(() => {
    const defaultKMRadius = 1;
    if (!popupLocationCoordinates?.coordinates?.is_approximate || !popupLocationCoordinates?.coordinates?.lon)
      return null;

    try {
      return turf.buffer(
        turf.point([popupLocationCoordinates.coordinates.lon, popupLocationCoordinates.coordinates.lat]),
        defaultKMRadius,
        { units: 'kilometers' }
      );
    } catch (error) {
      console.error('Error creating circular buffer:', error);
      return null;
    }
  }, [popupLocationCoordinates]);
};

const useSupercluster = (explorerType?: 'restaurants' | 'job-positions') => {
  const markers = useMarkers({ explorerType });
  const clusterRadius = useAtomValue(clusterRadiusAtom);
  const clusterMaxZoom = useAtomValue(clusterMaxZoomAtom);
  const clusterMinPoints = useAtomValue(clusterMinPointsAtom);

  const workerRef = useRef<Worker | null>(null);
  const [superClusterReady, setSuperClusterReady] = useState(false);

  useEffect(() => {
    if (!markers?.length) return;
    if (!workerRef.current) {
      workerRef.current = createSuperclusterWorker();
    }
    const worker = workerRef.current;
    setSuperClusterReady(false);

    worker.postMessage({
      type: 'load',
      payload: {
        options: {
          radius: clusterRadius,
          maxZoom: clusterMaxZoom,
          minPoints: clusterMinPoints,
        },
        points: markers.map(({ coordinates, ...rest }) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [coordinates.lon, coordinates.lat],
          },
          properties: null,
          extra: { ...rest },
        })),
      },
    });

    const handleLoaded = (e: MessageEvent) => {
      if (e.data.type === 'loaded') {
        setSuperClusterReady(true);
      }
    };
    worker.addEventListener('message', handleLoaded);

    return () => {
      worker.removeEventListener('message', handleLoaded);
      worker.terminate();
      workerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterRadius, clusterMaxZoom, clusterMinPoints, markers]);

  // Proxy object to interact with worker
  const superCluster = useMemo(() => {
    if (!superClusterReady || !workerRef.current) return null;
    return {
      getClusters: (bbox: number[], zoom: number) =>
        new Promise<any[]>((resolve) => {
          const worker = workerRef.current;
          // The worker may have been torn down (e.g. strict filters emptied the result set) while
          // this memoized proxy is still referenced by an in-flight handler. Resolve empty instead
          // of dereferencing null (`worker.addEventListener` on null crashed the page).
          if (!worker) return resolve([]);
          const handler = (e: MessageEvent) => {
            if (e.data.type === 'clusters') {
              worker.removeEventListener('message', handler);
              resolve(e.data.clusters);
            }
          };
          worker.addEventListener('message', handler);
          worker.postMessage({ type: 'getClusters', payload: { bbox, zoom } });
        }),
      getLeaves: (clusterId: number, limit: number, offset: number | null) =>
        new Promise<any[]>((resolve) => {
          const worker = workerRef.current;
          if (!worker) return resolve([]);
          const handler = (e: MessageEvent) => {
            if (e.data.type === 'leaves') {
              worker.removeEventListener('message', handler);
              resolve(e.data.leaves);
            }
          };
          worker.addEventListener('message', handler);
          worker.postMessage({ type: 'getLeaves', payload: { clusterId, limit, offset } });
        }),
      getClusterExpansionZoom: (clusterId: number) =>
        new Promise<number>((resolve) => {
          const worker = workerRef.current;
          if (!worker) return resolve(0);
          const handler = (e: MessageEvent) => {
            if (e.data.type === 'expansionZoom') {
              worker.removeEventListener('message', handler);
              resolve(e.data.zoom);
            }
          };
          worker.addEventListener('message', handler);
          worker.postMessage({ type: 'getClusterExpansionZoom', payload: { clusterId } });
        }),
    };
  }, [superClusterReady]);

  return { superCluster };
};

const useClusters = (explorerType?: 'restaurants' | 'job-positions') => {
  const [clusterData, setClusterData] = useState<any[]>([]);
  const mapRef = useAtomValue(getMapRefAtom);
  const popupLocationCoordinates = useAtomValue(getPopupLocationCoordinatesAtom);
  const { superCluster } = useSupercluster(explorerType);

  const handleClusters = useCallback(() => {
    const bounds = mapRef.current?.getBounds();
    const zoom = mapRef.current?.getZoom();

    if (bounds && superCluster) {
      superCluster
        .getClusters([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()], Math.floor(zoom))
        .then((clusters) => setClusterData(clusters))
        .catch((error) => console.error('Error clustering:', error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superCluster]);

  useEffect(() => {
    handleClusters();

    let moveEndTimeout: NodeJS.Timeout | null = null;
    const onMoveEnd = () => {
      if (moveEndTimeout) clearTimeout(moveEndTimeout);
      moveEndTimeout = setTimeout(() => {
        handleClusters();
      }, 250);
    };
    mapRef.current?.on('moveend', onMoveEnd);

    return () => {
      if (moveEndTimeout) clearTimeout(moveEndTimeout);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClusters]);

  const [leavesMap, setLeavesMap] = useState<Record<string | number, any[]>>({});

  useEffect(() => {
    let isMounted = true;
    const fetchLeaves = async () => {
      if (!superCluster) return;
      const newLeavesMap: Record<string | number, any[]> = {};
      for (const cluster of clusterData) {
        const clusterId = cluster?.properties?.['cluster_id'];
        const isCluster = cluster?.properties?.['cluster'];
        if (isCluster && clusterId) {
          try {
            const clusterCoordinates = await superCluster.getLeaves(
              +clusterId,
              explorerType === 'restaurants' ? 8 : Infinity,
              null
            );
            if (isMounted) newLeavesMap[clusterId] = clusterCoordinates || [];
          } catch {
            if (isMounted) newLeavesMap[clusterId] = [];
          }
        }
      }
      if (isMounted) setLeavesMap(newLeavesMap);
    };
    fetchLeaves();
    return () => {
      isMounted = false;
    };
  }, [clusterData, explorerType, superCluster]);

  const clusteredMarkers = useMemo(() => {
    return (
      <>
        {clusterData?.map((cluster: JobLocationCoordinatesClusterType) => {
          const { id: customClusterId, is_restaurant_marker: isRestaurantMarker } = cluster.extra || {};
          const clusterId = cluster?.properties?.['cluster_id'];
          const isCluster = cluster?.properties?.['cluster'];
          if (isCluster && clusterId) {
            const clusterCoordinates = leavesMap[clusterId] || [];
            const handleMarkerClick = async (e: any) => {
              const zoom = superCluster && (await superCluster.getClusterExpansionZoom(+cluster.id));
              mapRef.current?.flyTo({
                center: e.target.getLngLat(),
                zoom: zoom ? Math.min(zoom, 20) : 20,
                essential: true,
              });
            };
            const data = clusterCoordinates.map((leaf) => (
              <Marker
                key={`marker-${leaf?.['extra'].id}`}
                longitude={leaf.geometry.coordinates[0]}
                latitude={leaf.geometry.coordinates[1]}
                onClick={handleMarkerClick}
              >
                <div
                  className={
                    leaf?.['extra'].is_job_position_marker
                      ? 'h-4 w-4 bg-purple-700 opacity-90 rounded-full shadow-[0px_0px_8px_0px_rgba(0,0,26,0.25)] border border-neutral-10'
                      : 'h-4 w-4 bg-neutral-10 opacity-90 rounded-full shadow-[0px_0px_8px_0px_rgba(0,0,26,0.25)] border border-neutral-90'
                  }
                />
              </Marker>
            ));
            if (isRestaurantMarker) {
              if (clusterCoordinates.length > 4) return data;
              else
                return (
                  <Marker
                    key={`marker-${cluster.id}`}
                    longitude={cluster.geometry.coordinates[0]}
                    latitude={cluster.geometry.coordinates[1]}
                    onClick={handleMarkerClick}
                  >
                    <ClusterMarker limit={+cluster.properties.point_count} />
                  </Marker>
                );
            }
            return data;
          }

          return (
            <MarkerWithFloatingPopup
              key={`marker-${customClusterId}`}
              isOpen={popupLocationCoordinates?.id === customClusterId}
              cluster={{ ...cluster }}
              isRestaurantMarker={isRestaurantMarker}
            />
          );
        })}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterData, leavesMap, popupLocationCoordinates?.id, superCluster]);

  return clusteredMarkers;
};

export const useJobPositionClusters = () => {
  const clusteredMarkers = useClusters('job-positions');
  const bufferedArea = useBufferedArea();

  return useMemo(
    () => (
      <>
        {clusteredMarkers}
        {/* Guard against data={null}: MapLibre errors on a geojson source with null data. */}
        {bufferedArea && (
          <Source id="circular-radius" type="geojson" data={bufferedArea}>
            <Layer
              id="point-90-hi"
              type="fill"
              paint={{
                'fill-color': '#693eb5',
                'fill-opacity': 0.15,
              }}
            />
          </Source>
        )}
      </>
    ),
    [clusteredMarkers, bufferedArea]
  );
};

export const useRestaurantClusters = () => {
  const clusteredMarkers = useClusters('restaurants');
  return useMemo(() => <>{clusteredMarkers}</>, [clusteredMarkers]);
};

export const useCombinedClusters = (explorerType?: 'restaurants' | 'job-positions') => {
  const bufferedArea = useBufferedArea();
  const restaurantMarkers = useClusters('restaurants');
  const jobPositionMarkers = useClusters('job-positions');
  const isRestaurantMarker = useAtomValue(isRestaurantMarkerAtom);

  return useMemo(
    () => (
      <>
        {explorerType && explorerType === 'job-positions' ? (
          <>{jobPositionMarkers}</>
        ) : (
          <>
            {jobPositionMarkers}
            {restaurantMarkers}
          </>
        )}
        {!isRestaurantMarker && bufferedArea && (
          <Source id="circular-radius" type="geojson" data={bufferedArea}>
            <Layer
              id="point-90-hi"
              type="fill"
              paint={{
                'fill-color': '#693eb5',
                'fill-opacity': 0.15,
              }}
            />
          </Source>
        )}
      </>
    ),
    [explorerType, jobPositionMarkers, restaurantMarkers, bufferedArea, isRestaurantMarker]
  );
};
